import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from "uuid";

type AuthContextType = {
  currentUser: User | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAuthenticating: boolean;
  error: string | null;
  showBetaWelcome: boolean;
  setShowBetaWelcome: (show: boolean) => void;
  refreshSession: () => Promise<boolean>;
  isFirstTimeUser: boolean;
  setIsFirstTimeUser: (value: boolean) => void;
  checkSlackConnectionsAndRedirect: (user: User | null) => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  session: null,
  login: async () => {},
  signup: async () => {},
  resetPassword: async () => {},
  signOut: async () => {},
  isAuthenticating: false,
  error: null,
  showBetaWelcome: false,
  setShowBetaWelcome: () => {},
  refreshSession: async () => false,
  isFirstTimeUser: false,
  setIsFirstTimeUser: () => {},
  checkSlackConnectionsAndRedirect: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showBetaWelcome, setShowBetaWelcome] = useState<boolean>(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState<boolean>(false);
  // Track current user ID to detect user changes
  const [lastUserId, setLastUserId] = useState<string | null>(() => {
    return localStorage.getItem('lastUserId');
  });
  // Generate a session ID and store it in localStorage
  const [sessionId] = useState<string>(() => {
    const existing = localStorage.getItem('sessionId');
    if (existing) return existing;
    const newSessionId = uuidv4();
    localStorage.setItem('sessionId', newSessionId);
    return newSessionId;
  });
  const { toast } = useToast();

  const refreshSession = async (): Promise<boolean> => {
    try {
      console.log("Manually refreshing user session...");
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error("Failed to refresh session:", error);
        // If refresh fails, try to get current session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) {
          // Session is completely invalid, user needs to sign in again
          toast({
            variant: "destructive",
            title: "Session Expired",
            description: "Please sign in again to continue using calendar features.",
          });
          return false;
        }
      }
      
      if (session) {
        setSession(session);
        setCurrentUser(session.user);
        console.log("Session refreshed successfully");
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Error refreshing session:", error);
      return false;
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state change event:", event, "Session valid:", !!session);
        
        setSession(session);
        setCurrentUser(session?.user ?? null);
        
        if (event === 'SIGNED_IN') {
          const currentUserId = session?.user?.id;
          
          // Check if this is a different user signing in
          if (currentUserId && lastUserId && currentUserId !== lastUserId) {
            console.log("Different user detected, clearing chat history");
            // Clear all chat-related storage
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i);
              if (key?.startsWith('sharedmatter_chat_')) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => sessionStorage.removeItem(key));
            sessionStorage.removeItem('welcomeMessageShown');
            
            toast({
              title: "Welcome back!",
              description: "Chat history cleared for new user session.",
              duration: 3000,
            });
          }
          
          // Update the tracked user ID
          if (currentUserId) {
            setLastUserId(currentUserId);
            localStorage.setItem('lastUserId', currentUserId);
          }
          
          // Check if this is a new signup (user just confirmed their email)
          const wasSigningUp = localStorage.getItem('wasSigningUp');
          const isNewUser = localStorage.getItem('isFirstTimeLogin');
          
          if (wasSigningUp === 'true') {
            localStorage.removeItem('wasSigningUp');
            setShowBetaWelcome(true);
          }
          
          if (isNewUser === 'true') {
            localStorage.removeItem('isFirstTimeLogin');
            setIsFirstTimeUser(true);
            
            // Redirect to calendar page for first-time users
            setTimeout(() => {
              if (window.location.pathname !== '/calendar') {
                window.location.href = '/calendar?firstTime=true';
              }
            }, 1000);
          } else {
            // For existing users, check if they have calendar connections (only once per session)
            const hasShownCalendarSetup = sessionStorage.getItem('hasShownCalendarSetup');
            if (!hasShownCalendarSetup) {
              setTimeout(async () => {
                await checkCalendarConnectionsAndRedirect(session?.user);
              }, 1000);
            }
          }
          
          toast({
            title: "Signed in successfully",
            description: isNewUser === 'true' ? "Welcome to Shared Matter!" : "Welcome back!",
            duration: 3000,
          });
          
          console.log("Auth state changed: SIGNED_IN, user:", session?.user?.id);
          
          // Check if there's a pending Slack callback URL to redirect to
          const pendingSlackUrl = sessionStorage.getItem('slackCallbackUrl');
          if (pendingSlackUrl) {
            console.log("Found pending Slack callback URL:", pendingSlackUrl);
            sessionStorage.removeItem('slackCallbackUrl');
            // Use a small timeout to ensure the auth state is fully propagated
            setTimeout(() => {
              window.location.href = pendingSlackUrl;
            }, 500);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log("Auth state changed: SIGNED_OUT");
          setIsFirstTimeUser(false);
          
          // Clear all chat-related storage on sign out
          const keysToRemove = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key?.startsWith('sharedmatter_chat_')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => sessionStorage.removeItem(key));
          sessionStorage.removeItem('welcomeMessageShown');
          
          // Clear the tracked user ID on sign out
          setLastUserId(null);
          localStorage.removeItem('lastUserId');
          toast({
            title: "Signed out successfully",
            description: "You have been signed out.",
            duration: 3000,
          });
        } else if (event === 'TOKEN_REFRESHED') {
          console.log("Auth state changed: TOKEN_REFRESHED");
        }
      }
    );

    // Initialize auth state
    const initializeAuth = async () => {
      try {
        console.log("Initializing auth state...");
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          return;
        }
        
        setSession(session);
        setCurrentUser(session?.user ?? null);
        
        if (session?.user) {
          console.log("User already authenticated:", session.user.id);
        } else {
          console.log("No authenticated user found");
        }
      } catch (error) {
        console.error("Failed to initialize auth state:", error);
      }
    };
    
    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, [toast]);

  const checkCalendarConnectionsAndRedirect = async (user: User | null) => {
    if (!user) return;
    
    // Skip calendar check if already on calendar page or specific excluded pages
    const currentPath = window.location.pathname;
    const excludedPaths = ['/calendar', '/calendar/', '/auth', '/password-reset', '/slack'];
    if (excludedPaths.some(path => currentPath.startsWith(path))) {
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('calendar_connections')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);
        
      if (error) {
        console.error("Error checking calendar connections:", error);
        return;
      }
      
      // If user has no calendar connections, redirect to calendar page
      if (!data || data.length === 0) {
        // Mark that we've shown calendar setup for this session
        sessionStorage.setItem('hasShownCalendarSetup', 'true');
        
        toast({
          title: "Set up your calendars",
          description: "Connect your calendars to get the most out of Shared Matter. I can help you stay on top of your schedule!",
          duration: 5000,
        });
        
        setTimeout(() => {
          window.location.href = '/calendar?setup=true';
        }, 2000);
      }
    } catch (error) {
      console.error("Error checking calendar connections:", error);
    }
  };

  const checkSlackConnectionsAndRedirect = async (user: User | null) => {
    if (!user) return;
    
    // Skip Slack check if already on slack page or specific excluded pages
    const currentPath = window.location.pathname;
    const excludedPaths = ['/slack', '/slack/', '/auth', '/password-reset'];
    if (excludedPaths.some(path => currentPath.startsWith(path))) {
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('slack_workspaces')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);
        
      if (error) {
        console.error("Error checking Slack connections:", error);
        return;
      }
      
      // If user has no Slack connections, redirect to Slack page
      if (!data || data.length === 0) {
        toast({
          title: "Connect Slack next!",
          description: "Connect Slack to get notifications and stay updated on your conversations. I can help you stay connected!",
          duration: 5000,
        });
        
        setTimeout(() => {
          window.location.href = '/slack?setup=true';
        }, 2000);
      }
    } catch (error) {
      console.error("Error checking Slack connections:", error);
    }
  };

  const login = async (email: string, password: string) => {
    setIsAuthenticating(true);
    setError(null);
    
    try {
      console.log("Attempting to login user:", email);
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      console.log("Login successful for user:", data.user?.id);
    } catch (error: any) {
      setError(error.message);
      console.error("Login failed:", error.message);
      toast({
        variant: "destructive",
        title: "Login failed",
        description: error.message,
      });
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  const signup = async (email: string, password: string, firstName?: string, lastName?: string) => {
    setIsAuthenticating(true);
    setError(null);
    
    try {
      console.log("Attempting to sign up user:", email);
      
      // Set flag to indicate we're in signup process
      localStorage.setItem('wasSigningUp', 'true');
      localStorage.setItem('isFirstTimeLogin', 'true');
      
      // Create display name from first and last name
      const displayName = [firstName, lastName].filter(Boolean).join(' ');
      
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
            first_name: firstName || null,
            last_name: lastName || null,
          }
        }
      });
      
      if (error) throw error;
      
      // Create profile entry with first and last name
      if (data.user && (firstName || lastName)) {
        console.log("Creating profile for user:", data.user.id);
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            {
              id: data.user.id,
              first_name: firstName || null,
              last_name: lastName || null,
            }
          ]);
        
        if (profileError) {
          console.error("Profile creation error:", profileError);
          // Don't throw here as the user account was created successfully
        } else {
          console.log("Profile created successfully");
        }
      }
      
      // Check if email confirmation is required
      if (data.user && !data.session) {
        // User needs to confirm email
        toast({
          title: "Account created",
          description: "Please check your email to confirm your account.",
        });
        console.log("Signup successful, email confirmation required for:", email);
      } else if (data.user && data.session) {
        // User is immediately signed in (confirmations disabled)
        console.log("User created and signed in immediately");
        setShowBetaWelcome(true);
        localStorage.removeItem('wasSigningUp');
        toast({
          title: "Account created successfully",
          description: "Welcome to Shared Matter!",
        });
      }
      
    } catch (error: any) {
      localStorage.removeItem('wasSigningUp');
      setError(error.message);
      console.error("Signup failed:", error.message);
      toast({
        variant: "destructive",
        title: "Signup failed",
        description: error.message,
      });
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const resetPassword = async (email: string) => {
    setIsAuthenticating(true);
    setError(null);
    
    try {
      console.log("Sending password reset email to:", email);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/password-reset`,
      });
      
      if (error) throw error;
      
      toast({
        title: "Password reset email sent",
        description: "Please check your email for password reset instructions.",
      });
      
      console.log("Password reset email sent successfully");
    } catch (error: any) {
      setError(error.message);
      console.error("Password reset failed:", error.message);
      toast({
        variant: "destructive",
        title: "Password reset failed",
        description: error.message,
      });
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  const signOut = async () => {
    try {
      console.log("Signing out user");
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      console.log("Sign out successful");
    } catch (error: any) {
      setError(error.message);
      console.error("Sign out failed:", error.message);
      toast({
        variant: "destructive",
        title: "Sign out failed",
        description: error.message,
      });
    }
  };

  const value = {
    currentUser,
    session,
    login,
    signup,
    resetPassword,
    signOut,
    isAuthenticating,
    error,
    showBetaWelcome,
    setShowBetaWelcome,
    refreshSession,
    isFirstTimeUser,
    setIsFirstTimeUser,
    checkSlackConnectionsAndRedirect
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
